import React, { useState } from 'react';
import { capitalize } from '@material-ui/core';
import I18n from '@/modules/i18n';
import cn from '@/utils/cn';
import Card from '@/components/ui/Card';
import Icon from '@/components/ui/Icon';
import Text from '@/components/ui/Text';
import { UnstyledButton } from '@/components/ui/Buttons';
import { IRubric } from '@/components/staffer/ExerciseBuilder/Rubric/useQuestionRubric';
import EditRubricButton from '@/components/staffer/ExerciseBuilder/Rubric/EditRubricCard/EditRubricButton';
import DestroyRubricButton from '@/components/staffer/ExerciseBuilder/Rubric/EditRubricCard/DestroyRubricButton';
import RubricPreview from '@/components/staffer/ExerciseBuilder/Rubric/EditRubricCard/RubricPreview';
import { IRubricModalQuestion, IRubricModalQuestionTranslation } from '../AddRubricModal/AddRubricModal.types';

interface IEditRubricCardProps {
  question: IRubricModalQuestion;
  translations: [IRubricModalQuestionTranslation<'en'>, IRubricModalQuestionTranslation<'es'>];
  rubric: IRubric;
}

const COPY_CONFIRMATION_DURATION_MS = 2000;

const EditRubricCard = ({ question, rubric, translations }: IEditRubricCardProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopyId = () => {
    navigator.clipboard.writeText(String(rubric.id)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_CONFIRMATION_DURATION_MS);
    });
  };

  return (
    <Card className={cn(
      'tw-w-[366px] tw-h-fit tw-min-h-[92px] hover:tw-shadow-md',
      'tw-border-2 tw-rounded-lg tw-border-gray-300 tw-border-solid tw-flex tw-items-center',
    )}
    >
      <Card.Body className="tw-w-full tw-flex tw-items-center tw-py-2 tw-px-3 tw-gap-2">
        <div
          className={cn(
            'tw-w-1/4 tw-pr-3 tw-border-r-2 tw-border-solid tw-border-gray-300 tw-py-1.5',
            'tw-flex tw-items-center tw-justify-center',
          )}
        >
          <RubricPreview
            useThumbnail
            holisticRubricProps={{
              classes: {
                cell: 'tw-p-1 tw-text-[2px]',
              },
            }}
            rubric={rubric}
          />
        </div>
        <div className="tw-grow tw-flex tw-flex-col tw-gap-1">
          <div className="tw-flex tw-items-center tw-justify-between">
            <span>
              <span className="tw-font-bold">Type:</span>
              {' '}
              {capitalize(rubric.rubric_type)}
            </span>
            <div className="tw-flex tw-gap-3 tw-items-center">
              <EditRubricButton question={question} translations={translations} />
              <DestroyRubricButton question={question} rubric={rubric} />
            </div>
          </div>
          <div className="tw-flex tw-items-center tw-gap-1">
            <Text size="sm">
              <span className="tw-font-semibold">ID:</span>
              {' '}
              {rubric.id}
            </Text>
            <UnstyledButton
              aria-label={I18n.t('rubric_id_copied')}
              className="!tw-cursor-pointer tw-flex tw-items-center"
              title={copied ? I18n.t('rubric_id_copied') : I18n.t('copy_rubric_id')}
              onClick={handleCopyId}
            >
              <Icon
                className={cn('tw-pointer-events-none', copied ? 'tw-text-green-600' : 'tw-text-gray-500')}
                icon="Copy"
                size={14}
              />
            </UnstyledButton>
            {copied && (
              <Text className="!tw-text-green-600" size="sm">{I18n.t('rubric_id_copied')}</Text>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default EditRubricCard;
